// Entry point and frame loop.
//
// The simulation is a deterministic fixed-timestep 24 Hz stepper that knows
// nothing about the DOM or Three.js. Everything below is a read-only view of it
// plus an input layer that pushes commands back in. Rendering interpolates
// between logic frames (world snapshots e.px/e.py each tick) so a 24 Hz sim
// displays smoothly at whatever rate the monitor runs.
//
// MODULE CONTRACT -- the interfaces the render/ui modules must implement:
//
//   new Renderer(canvas, world, player)
//     .render(alpha)                 alpha in [0,1): lerp factor px->x
//     .resize()
//     .screenToWorld(sx, sy)         -> { x, y } tile coords on the ground plane
//     .worldToScreen(x, y, z=0)      -> { x, y, behind }
//                                    z is height ABOVE THE TERRAIN SURFACE at
//                                    (x,y), not absolute elevation, so that it
//                                    is the true inverse of screenToWorld.
//     .setSelection(idsArray)
//     .setPlacement(defId|null, x, y, valid)   translucent building ghost
//     .camera                        -> RTSCamera
//
//   new RTSCamera(world)             (owned by render/camera.js, used via renderer.camera)
//     .update(dt, keys, pointerEdge)
//     .centerOn(x, y)
//     .viewRect()                    -> { x0, y0, x1, y1 } world tiles on screen
//
//   new Minimap({ canvas, world, player, renderer })
//     .render()
//     .hitTest(px, py)               -> { x, y } world tiles, or null
//
//   new HUD({ root, world, player, input })
//     .update()
//     .minimapCanvas                 -> HTMLCanvasElement the Minimap draws into
//     .flash(message)
//
//   new InputController({ canvas, world, renderer, hud, minimap, player })
//     .update(dt)
//     .selection                     -> Set<entityId>
//     .controlGroups                 -> Array<number[]> length 10

import { World } from './sim/world.js';
import { TICK_MS, PLAYER } from './sim/constants.js';
import { Renderer } from './render/scene.js';
import { Minimap } from './render/minimap.js';
import { HUD } from './ui/hud.js';
import { InputController } from './ui/input.js';

const canvas = document.getElementById('gl');
const hudRoot = document.getElementById('hud');

const seed = Number(new URLSearchParams(location.search).get('seed') || 1337);
const world = new World({ seed, aiEnabled: true });

const renderer = new Renderer(canvas, world, PLAYER);
const hud = new HUD({ root: hudRoot, world, player: PLAYER });
const minimap = new Minimap({ canvas: hud.minimapCanvas, world, player: PLAYER, renderer });
const input = new InputController({ canvas, world, renderer, hud, minimap, player: PLAYER });
hud.input = input;

renderer.camera.centerOn(world.startLocations[PLAYER].x, world.startLocations[PLAYER].y);

addEventListener('resize', () => renderer.resize());

// Expose for the headless/browser smoke tests and for debugging in the console.
globalThis.GAME = { world, renderer, hud, input, minimap };

let acc = 0;
let last = performance.now();
let paused = false;
addEventListener('keydown', (e) => { if (e.key === 'F10') { paused = !paused; e.preventDefault(); } });

function frame(now) {
  const dt = Math.min(0.25, (now - last) / 1000);
  last = now;

  if (!paused) {
    acc += dt * 1000;
    // Cap catch-up so a background tab does not spiral; the sim simply loses time.
    let steps = 0;
    while (acc >= TICK_MS && steps < 5) { world.step(); acc -= TICK_MS; steps++; }
    if (steps === 5) acc = 0;
  }

  input.update(dt);
  renderer.render(paused ? 1 : acc / TICK_MS);
  minimap.render();
  hud.update();

  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);
